import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AccountRestrictionDTO from 'features/accounts/dtos/AccountRestrictionDTO';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import { Nullable } from 'features/shared/types/nullable';
import React, { useState } from 'react';
import AccountRestriction from 'features/accounts/components/AccountRestriction';
import AccountRestrictionSelect from 'features/accounts/components/AccountRestrictionSelect';
import DataGridRow from 'features/shared/components/dataGrid/DataGridRow';
import styles from './accountRestrictionsSubForm.module.scss';
import Alert from 'features/shared/alerts/components/Alert';
import { useTranslation } from 'react-i18next';

interface Props {
  contracts: ContractDropdownDTO[];
  contractualAccounts: ContractualAccountDTO[];
  addedAccountRestrictions: AccountRestrictionDTO[];
  setAddedAccountRestrictions: React.Dispatch<React.SetStateAction<AccountRestrictionDTO[]>>;
}

const AccountRestrictionsSubForm = ({ contracts, contractualAccounts, addedAccountRestrictions, setAddedAccountRestrictions }: Props) => {
  // translations
  const tLabels = useTranslation("accounts\\accounts").t;

  // state
  const [actualAccountRestriction, setActualAccountRestriction] = useState<Nullable<AccountRestrictionDTO>>({entity: "ContractualAccount"}); // preset AccountRestrictionType to 'ContractrualAccount'

  // helpers
  const filterDataByRestrictions = <T, >(data: T[], filterProperty: string): T[] => {
    if (addedAccountRestrictions.length > 0) {
      return data.filter((x) =>
        !addedAccountRestrictions.some(
          (y) => (x as any)[filterProperty] === (y as any)[filterProperty]
        )
      );
    }
    return data;
  };

  const disableAddButton = (): boolean => {
    if (actualAccountRestriction) {
      return (
        actualAccountRestriction.contractualAccountId === undefined &&
        actualAccountRestriction.placeOfDeliveryId === undefined
      );
    }

    return true;
  }

  // handlers
  const addRestrictionHandler = () => {
    if (actualAccountRestriction) {
      let newActualAccountRestricion: AccountRestrictionDTO = {
        entity: actualAccountRestriction.entity
      };

      setAddedAccountRestrictions([...addedAccountRestrictions, actualAccountRestriction]); 
      setActualAccountRestriction(newActualAccountRestricion);
    }
  }

  const deleteRestrictionHandler = (restriction: AccountRestrictionDTO) => {
    let filteredAddedRestricitons = addedAccountRestrictions.filter(
      (x) =>
        x.contractualAccountId !== restriction.contractualAccountId ||
        x.placeOfDeliveryId !== restriction.placeOfDeliveryId
    );

    setAddedAccountRestrictions(filteredAddedRestricitons);
  };

  // sub-renders
  const renderAddedRestrictions = () => {
    if (addedAccountRestrictions.length > 0) {
      return (
        <div className="row no-gutters mx-n1">
          {addedAccountRestrictions.map((item, index) => (
            <div className="col-lg-6 px-1">
              <DataGridRow key={index}>
                <AccountRestriction
                  restriction={item}
                  onDeleteClick={deleteRestrictionHandler}
                />
              </DataGridRow>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className="text-muted font-italic mt-n2">
          {tLabels("accountRestrictionsSubForm.noRestrictionsAdded")}
        </div>
      );
    }
  }

  // render
  return (
    <div className={styles.box}>
      <div className="row no-gutters align-items-end">
        <div className="col-lg">
          <AccountRestrictionSelect
            contracts={filterDataByRestrictions(contracts, "placeOfDeliveryId")}
            contractualAccounts={filterDataByRestrictions(contractualAccounts, "contractualAccountId")}
            selectedRestriction={actualAccountRestriction}
            setSelectedRestrictiton={setActualAccountRestriction}
          />
        </div>
        <div className="col-auto pl-lg-2">
          <button
            type="button"
            className="btn btn-secondary mb-3"
            onClick={addRestrictionHandler}
            disabled={disableAddButton()}
          >
            <span className="d-lg-none">{tLabels("accountRestrictionsSubForm.addRestriction")}</span>
            <FontAwesomeIcon icon="plus" className="d-none d-lg-inline-block" />
          </button>
        </div>
      </div>
      <div className="mt-2">
        {renderAddedRestrictions()}  
      </div>
  </div>
  )
}

export default AccountRestrictionsSubForm;
